import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2, Edit, Send, Library } from "lucide-react";

function SchedulePanel({ selected, setSelected }) {
  const [schedules, setSchedules] = useState([]);
  const [loadErr, setLoadErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gameType, setGameType] = useState("rebus");
  const [start, setStart] = useState(() => new Date(Date.now() + 60000).toISOString().slice(0,16));
  const [end, setEnd] = useState(() => new Date(Date.now() + 3*3600000).toISOString().slice(0,16));
  // sudoku/word libraries
  const [lib, setLib] = useState([]);
  const [libSel, setLibSel] = useState([]);
  const [genCount, setGenCount] = useState(1);
  const [genDiff, setGenDiff] = useState("medium");
  const GAMES = [["rebus", "Rebus"], ["sudoku", "Sudoku"], ["word", "Word Building"]];

  const load = async (retries = 2) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/schedule");
      setSchedules(r.data || []);
      setLoadErr(false);
    } catch (e) {
      // Transient backend/health blip — retry a couple times before surfacing
      // an error, so the panel is never left permanently blank.
      if (retries > 0) { setTimeout(() => load(retries - 1), 1500); return; }
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  };
  const loadLib = async (gt) => {
    try {
      if (gt === "rebus") { setLib([]); return; }
      if (gt === "word") {
        const r = await api.get("/admin/word/bank");
        const items = [
          ...(r.data.custom || []).map((w) => ({ id: w.word, word: w.word, meaning: w.meaning, builtin: false })),
          ...(r.data.builtin || []).map((w) => ({ id: w.word, word: w.word, meaning: w.meaning, builtin: true })),
        ];
        setLib(items); setLibSel([]); return;
      }
      const r = await api.get("/admin/sudoku/puzzles"); setLib(r.data); setLibSel([]);
    } catch (e) {
      setLib([]);
    }
  };
  useEffect(()=>{ load(); }, []);
  useEffect(()=>{ loadLib(gameType); /* eslint-disable-next-line */ }, [gameType]);

  const toggleSel = (id) => setLibSel((s) => s.includes(id) ? s.filter((x)=>x!==id) : [...s, id]);

  const genSudoku = async () => {
    await api.post("/admin/sudoku/puzzles/generate", { count: Number(genCount)||1, difficulty: genDiff });
    toast.success("Sudoku puzzle(s) generated"); loadLib("sudoku");
  };
  const delLib = async (id) => {
    const path = `/admin/sudoku/puzzles/${id}`;
    await api.delete(path); loadLib(gameType);
  };

  const push = async () => {
    const startISO = new Date(start).toISOString();
    const endISO = new Date(end).toISOString();
    if (new Date(end) <= new Date(start)) { toast.error("End time must be after start time"); return; }
    if (gameType === "rebus") {
      if (selected.length === 0) { toast.error("Pick at least one puzzle from Library"); return; }
      for (const pid of selected) {
        await api.post("/admin/schedule", { game_type: "rebus", puzzle_ids: [pid], start_time: startISO, end_time: endISO });
      }
      toast.success(`Pushed ${selected.length} Rebus puzzle(s) live`);
      setSelected([]);
    } else if (gameType === "word") {
      if (libSel.length === 0) { toast.error("Select at least one word from the Word Bank"); return; }
      await api.post("/admin/schedule", { game_type: "word", words: libSel, start_time: startISO, end_time: endISO });
      toast.success(`Scheduled ${libSel.length} word(s) live`);
      setLibSel([]);
    } else {
      if (libSel.length === 0) { toast.error("Select at least one Sudoku puzzle"); return; }
      await api.post("/admin/schedule", { game_type: gameType, puzzle_ids: libSel, start_time: startISO, end_time: endISO });
      toast.success("Scheduled Sudoku live");
      setLibSel([]);
    }
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    await api.delete(`/admin/schedule/${id}`); load();
  };

  const [editId, setEditId] = useState(null);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const toInput = (iso) => { const d = new Date(iso); return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
  const startEdit = (s) => { setEditId(s.id); setEditStart(toInput(s.start_time)); setEditEnd(toInput(s.end_time)); };
  const saveEdit = async () => {
    if (new Date(editEnd) <= new Date(editStart)) { toast.error("End time must be after start time"); return; }
    try {
      await api.patch(`/admin/schedule/${editId}`, { start_time: new Date(editStart).toISOString(), end_time: new Date(editEnd).toISOString() });
      toast.success("Schedule updated"); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-6">
      {loadErr && (
        <div data-testid="schedule-load-error" className="brutal-card p-4 bg-[#FFE8E8] border-red-500 flex items-center justify-between gap-3">
          <span className="text-sm font-bold text-red-700">Couldn't load schedules (temporary connection issue).</span>
          <button data-testid="schedule-retry" onClick={() => load()} className="brutal-btn text-xs">Retry</button>
        </div>
      )}
      <div className="brutal-card p-5">
        <h3 className="font-black uppercase text-lg mb-3 flex items-center gap-2"><Send size={18}/> Schedule a Game Live</h3>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="schedule-game-select">
          {GAMES.map(([k,label]) => (
            <button key={k} data-testid={`sched-game-${k}`} onClick={()=>setGameType(k)} className={`px-4 py-2 border-2 border-black font-black uppercase text-xs ${gameType===k ? "bg-black text-white" : "bg-white hover:bg-[#FFD600]"}`}>{label}</button>
          ))}
        </div>

        {gameType === "rebus" ? (
          <p className="text-sm mb-3">Selected from Library tab: <strong>{selected.length}</strong> puzzle(s). Each is scheduled as its own live round.</p>
        ) : gameType === "word" ? (
          <div className="mb-4 border-2 border-black p-3" data-testid="sched-word-bank">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="font-black uppercase text-sm">Word Bank <span className="text-neutral-400">({lib.length})</span></div>
              <p className="text-[11px] text-neutral-500">Pick a curated word to push live. Manage the list in the <strong>Word Bank</strong> tab.</p>
            </div>
            {lib.length === 0 ? (
              <p className="text-xs text-neutral-500">No words in the bank yet — add some in the Word Bank tab.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                {lib.map((p) => {
                  const on = libSel.includes(p.id);
                  return (
                    <div key={p.id} data-testid={`wordbank-item-${p.word}`} onClick={()=>toggleSel(p.id)} title={p.meaning || ""} className={`border-2 border-black p-2 cursor-pointer ${on ? "bg-[#39FF14]" : "bg-white hover:bg-[#FFFCE5]"}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-black text-sm truncate uppercase">{p.word}</span>
                        {p.builtin
                          ? <span className="text-[9px] uppercase font-black border border-neutral-300 px-1 text-neutral-400 flex-shrink-0">Built-in</span>
                          : <span className="text-[9px] uppercase font-black border border-black bg-[#FFD600] px-1 flex-shrink-0">Custom</span>}
                      </div>
                      {p.meaning && <div className="text-[10px] text-neutral-500 truncate mt-0.5">{p.meaning}</div>}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-2">Selected: <strong>{libSel.length}</strong>. The scrambled letters of the chosen word become the day's tray; its meaning shows on the answer reveal. When multiple are scheduled together the first goes live — use separate time windows to rotate them.</p>
          </div>
        ) : (
          <div className="mb-4 border-2 border-black p-3" data-testid="sched-library">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="font-black uppercase text-sm">Sudoku Library <span className="text-neutral-400">({lib.length})</span></div>
              <div className="flex flex-wrap items-end gap-2">
                <div><label className="text-[10px] font-black uppercase block">Count</label><input data-testid="gen-count" type="number" min={1} max={25} value={genCount} onChange={(e)=>setGenCount(e.target.value)} className="brutal-input w-20"/></div>
                <div><label className="text-[10px] font-black uppercase block">Difficulty</label>
                  <select data-testid="gen-difficulty" value={genDiff} onChange={(e)=>setGenDiff(e.target.value)} className="brutal-input w-28"><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option></select>
                </div>
                <button data-testid="gen-sudoku-btn" onClick={genSudoku} className="brutal-btn text-sm flex items-center gap-1"><Sparkles size={14}/> Generate</button>
              </div>
            </div>
            {lib.length === 0 ? (
              <p className="text-xs text-neutral-500">No items yet — generate some above.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {lib.map((p) => {
                  const on = libSel.includes(p.id);
                  return (
                    <div key={p.id} data-testid={`lib-item-${p.id}`} onClick={()=>toggleSel(p.id)} className={`border-2 border-black p-2 cursor-pointer flex items-center justify-between gap-1 ${on ? "bg-[#39FF14]" : "bg-white"}`}>
                      <span className="font-black text-sm truncate">Sudoku · {p.difficulty}</span>
                      <button data-testid={`lib-del-${p.id}`} onClick={(e)=>{e.stopPropagation(); delLib(p.id);}} className="p-1 border-2 border-black hover:bg-[#FF3B30] hover:text-white"><Trash2 size={11}/></button>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-2">Selected: <strong>{libSel.length}</strong>. When multiple are scheduled together the first goes live; schedule separate time windows to run them one after another.</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-black uppercase">Start (local)</label>
            <input data-testid="schedule-start" type="datetime-local" value={start} onChange={(e)=>setStart(e.target.value)} className="brutal-input"/>
          </div>
          <div>
            <label className="text-xs font-black uppercase">End (local)</label>
            <input data-testid="schedule-end" type="datetime-local" value={end} onChange={(e)=>setEnd(e.target.value)} className="brutal-input"/>
          </div>
        </div>
        <button data-testid="push-live-btn" onClick={push} className="brutal-btn mt-4">Push Live</button>
      </div>

      <div>
        <h3 className="font-black uppercase mb-3">Live & Past Schedules</h3>
        <div className="space-y-3">
          {schedules.length === 0 && <div className="text-sm text-neutral-500">No schedules yet.</div>}
          {schedules.map((s) => {
            const now = new Date();
            const status = new Date(s.end_time) < now ? "Ended" : new Date(s.start_time) > now ? "Upcoming" : "Live";
            const gt = s.game_type || "rebus";
            const gtLabel = { rebus: "Rebus", sudoku: "Sudoku", word: "Word Building" }[gt] || gt;
            return (
              <div key={s.id} data-testid={`schedule-row-${s.id}`} className="brutal-card p-4 flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex gap-2 items-center flex-wrap">
                    <span className={`px-2 py-0.5 border-2 border-black font-black text-xs uppercase ${status === "Live" ? "bg-[#39FF14]" : status === "Upcoming" ? "bg-[#FFD600]" : "bg-neutral-200"}`}>{status}</span>
                    <span className="px-2 py-0.5 border-2 border-black font-black text-xs uppercase bg-[#1E88E5] text-white">{gtLabel}</span>
                    <span className="font-mono text-xs text-neutral-500">{new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    {(s.items || []).map((it, i) => (
                      <div key={i} className="border-l-4 border-[#FF3B30] pl-3 my-1">
                        <strong>{it.label}</strong> {it.sub ? <span className="text-neutral-500">— {it.sub}</span> : null}
                      </div>
                    ))}
                  </div>
                  {editId === s.id && (
                    <div className="mt-3 border-2 border-black p-3 bg-[#FFFCE5]" data-testid={`schedule-edit-${s.id}`}>
                      <div className="text-[11px] font-black uppercase mb-2">Reschedule (local time)</div>
                      <div className="flex flex-wrap gap-3 items-end">
                        <div>
                          <label className="text-[10px] font-black uppercase block">Start</label>
                          <input data-testid={`edit-start-${s.id}`} type="datetime-local" value={editStart} onChange={(e)=>setEditStart(e.target.value)} className="brutal-input"/>
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase block">End</label>
                          <input data-testid={`edit-end-${s.id}`} type="datetime-local" value={editEnd} onChange={(e)=>setEditEnd(e.target.value)} className="brutal-input"/>
                        </div>
                        <button data-testid={`edit-save-${s.id}`} onClick={saveEdit} className="brutal-btn text-sm">Save</button>
                        <button data-testid={`edit-cancel-${s.id}`} onClick={()=>setEditId(null)} className="brutal-btn-secondary text-sm">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button data-testid={`schedule-edit-btn-${s.id}`} onClick={()=>startEdit(s)} title="Edit schedule" className="p-2 border-2 border-black hover:bg-[#FFD600]"><Edit size={14}/></button>
                  <button data-testid={`schedule-del-btn-${s.id}`} onClick={()=>remove(s.id)} title="Delete schedule" className="p-2 border-2 border-black hover:bg-[#FF3B30] hover:text-white"><Trash2 size={14}/></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SchedulePanel;
